// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 zouwei

/**
 * `.mboard.svg` scene payload codec (spec §4.2, teaching-whiteboard.md).
 *
 * A board file is a self-contained SVG: the rendered graphics are ordinary
 * SVG content (viewable anywhere), and the editable scene JSON travels in an
 * SVG <metadata> comment — same mechanism Excalidraw uses for its
 * embed-scene PNG/SVG exports.
 *
 * Encoding: scene JSON → deflate (fflate) → base64, wrapped in explicit
 * start/end markers so extraction is a plain string scan (no XML parser
 * needed, works identically in browser hosts and Node tooling).
 */

import { deflateSync, inflateSync, strFromU8, strToU8 } from 'fflate'
import { isBoardScene } from './scene'
import type { BoardScene } from './types'

export const PAYLOAD_MIME = 'application/vnd.moraya-board+json'
const PAYLOAD_START = 'payload-start:'
const PAYLOAD_END = ':payload-end'

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/** scene → base64(deflate(JSON)) */
export function encodeScenePayload(scene: BoardScene): string {
  return bytesToBase64(deflateSync(strToU8(JSON.stringify(scene))))
}

/** Inverse of {@link encodeScenePayload}. Throws on malformed input. */
export function decodeScenePayload(payload: string): BoardScene {
  const json = strFromU8(inflateSync(base64ToBytes(payload)))
  const scene: unknown = JSON.parse(json)
  if (!isBoardScene(scene)) {
    throw new Error('Not a moraya-board scene payload (or unsupported version)')
  }
  return scene
}

/** The <metadata> block embedded into an exported `.mboard.svg`. */
export function sceneMetadataBlock(scene: BoardScene): string {
  return [
    '<metadata id="mboard">',
    `<!-- payload-type:${PAYLOAD_MIME};version:1;encoding:base64+deflate -->`,
    `<!-- ${PAYLOAD_START}${encodeScenePayload(scene)}${PAYLOAD_END} -->`,
    '</metadata>',
  ].join('\n')
}

/**
 * Recover the scene from `.mboard.svg` text. Returns null when the file has
 * no payload (e.g. metadata stripped by an SVG optimizer) or the payload is
 * corrupt — callers show the "view-only, scene data lost" fallback.
 */
export function extractSceneFromSvg(svgText: string): BoardScene | null {
  const start = svgText.indexOf(PAYLOAD_START)
  if (start === -1) return null
  const from = start + PAYLOAD_START.length
  const end = svgText.indexOf(PAYLOAD_END, from)
  if (end === -1) return null
  try {
    return decodeScenePayload(svgText.slice(from, end).trim())
  } catch {
    return null
  }
}
